import { useState } from 'react';
import {
  GetIdCardsUrlParams,
  IdCardUserRow,
  PhotoIdCardsApiResponse,
  UseIdCardsInternalArgs
} from '../types/IdCardTypes';
import idCardsApis from '../../../apis/idCards';
import { SnackbarSeverityType } from '../../../enums';
import { defaultAcademicYear } from '../../../constants/academicYears';
import { GeneratePdfArgs } from '../../../types/idCardsType';
import { useSchoolClassesConfigHook } from '../../common/hooks/useSchoolClassesConfig';
import { downloadFileFromUrl } from '../../../utils/fileGeneration';
import getDomain from '../../../utils/getBaseUrl';

export const useIdCards = ({ showToast }: UseIdCardsInternalArgs) => {
  const [academicYear, setAcademicYear] = useState(defaultAcademicYear);
  const [grade, setGrade] = useState('');
  const [division, setDivision] = useState('');
  const [typeOfRelation, setTypeOfRelation] = useState('');
  const [name, setName] = useState('');

  const [rows, setRows] = useState<IdCardUserRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [pdfGenerating, setPdfGenerating] = useState(false);

  const { gradeOptions, divisionOptions } = useSchoolClassesConfigHook();

  const canSearch = academicYear && grade && division && typeOfRelation;
  const isNonStudentRelation = typeOfRelation && typeOfRelation !== 'STUDENT';

  const search = async (schoolId: string) => {
    if (!canSearch) return;
    try {
      setLoading(true);
      const response: PhotoIdCardsApiResponse =
        await idCardsApis.getPhotoIdCards({
          school: schoolId,
          academicYear,
          grade,
          division,
          typeOfRelation,
          name
        });
      setRows(
        (response.userInfo || []).map((user) => {
          return {
            id: user.studentId,
            enrollmentNumber: user.studentEnrollmentNo || '',
            name: user.name,
            studentName: isNonStudentRelation ? user.studentName : undefined,
            photoUrl: user.profile || undefined
          } as IdCardUserRow;
        })
      );
      showToast('Fetched ID card records', SnackbarSeverityType.success);
    } catch (e: any) {
      const message = e?.message || 'Failed to fetch records';
      showToast(message, SnackbarSeverityType.error);
    } finally {
      setLoading(false);
    }
  };

  const getIdCardsUrl = ({
    academicYear,
    school,
    grade,
    division,
    typeOfRelation,
    name,
    selectedIds
  }: GetIdCardsUrlParams) => {
    const studentIdsString = selectedIds.join(',');
    const base64StudentIds = btoa(studentIdsString);
    const params: Record<string, string> = {
      ...(academicYear && { ay: academicYear }),
      ...(school && { school }),
      ...(grade && { grade }),
      ...(division && { division }),
      ...(typeOfRelation && { typeOfRelation }),
      ...(name && { name }),
      ...(selectedIds.length > 0 && { studentIds: base64StudentIds })
    };
    const searchParams = new URLSearchParams(params);
    return `${getDomain(school, 'reactAppEtherBaseUrl')}/icards/?${searchParams.toString()}`;
  };

  const generatePdf = async (schoolId: string, selectedIds: string) => {
    const studentIds = btoa(selectedIds);
    const apiPayload: GeneratePdfArgs = {
      school: schoolId,
      academicYear,
      grade,
      division,
      typeOfRelation,
      name,
      studentIds
    };

    try {
      setPdfGenerating(true);
      const response = await idCardsApis.generatePhotoIdCardsPdf(apiPayload);
      downloadFileFromUrl(response.photoIdCardUrl, response.fileName, 'pdf');
      showToast('PDF generation started', SnackbarSeverityType.success);
    } catch (e: any) {
      console.error('generatePdf API error:', e, e?.response?.data);
      showToast(
        e?.message || 'Failed to generate PDF',
        SnackbarSeverityType.error
      );
    } finally {
      setPdfGenerating(false);
    }
  };

  const filteredGrades = gradeOptions.filter(
    (grade: { label: string; value: string }) =>
      !grade?.label?.includes('-INCOMING')
  );

  return {
    academicYear,
    setAcademicYear,
    grade,
    setGrade,
    division,
    setDivision,
    typeOfRelation,
    setTypeOfRelation,
    name,
    setName,
    rows,
    loading,
    pdfGenerating,
    canSearch,
    search,
    generatePdf,
    gradeOptions: filteredGrades,
    divisionOptions,
    getIdCardsUrl
  };
};
